import { useEffect, useRef, useState } from 'react';

/**
 * Mount content when an element enters (or nears) the viewport.
 * Falls back to immediate mount when IntersectionObserver is unavailable.
 */
export function useIntersectionLazyMount({
  root = null,
  rootMargin = '400px 0px',
  threshold = 0,
  once = true,
  disabled = false,
} = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(disabled);

  useEffect(() => {
    if (disabled) {
      setIsVisible(true);
      return undefined;
    }

    const element = ref.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setIsVisible(false);
          }
        });
      },
      { root, rootMargin, threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [root, rootMargin, threshold, once, disabled]);

  return { ref, isVisible };
}
